import { useQueryClient } from '@tanstack/react-query'
import { AiIcon } from 'assets/icons/AiIcon'
import { DeleteIcon } from 'assets/icons/DeleteIcon'
import { FreeTextItemIcon } from 'assets/icons/FreeTextItemIcon'
import { UploadIcon } from 'assets/icons/UploadIcon'
import { Alert } from 'components/Alert/Alert'
import { Badge } from 'components/Badge/Badge'
import { Card, CardBody } from 'components/Card/Card'
import { FileInput, Input, Textarea } from 'components/Form'
import { UploadedFile } from 'components/Form/UploadedFile'
import { Spinner } from 'components/Loading/Loading'
import { NavBar } from 'components/NavBar/NavBar'
import { AsyncAutocomplete } from 'components/Select/AsyncAutocomplete'
import { CostCenterSelect } from 'components/Select/CostCenter.select'
import { DepartmentSelect } from 'components/Select/Department.select'
import { InventorySupplierSelect } from 'components/Select/InventorySupplier.select'
import { Option, Select } from 'components/Select/Select'
import { UnitSelect } from 'components/Select/UnitSelect'
import { PROCUREMENT_PRIORITY_META } from 'constants/meta'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Button,
  colors,
  Flex,
  H1WithBack,
  Icon,
  OutlineButton,
  PageWrapper,
} from 'styling/GlobalStyles'
import { ColorVariant, Heading2, MediumText } from 'styling/TypographyV2'
import { IInventoryItemSingleResponse } from 'types/api/inventoryItems.interface'
import {
  CreateProcurementPurchaseRequestDto,
  ProcurementPriority,
  ProcurementPurchaseRequestDetailsResponse,
  ProcurementPurchaseRequestListResponse,
} from 'types/procurement'
import { PurchaseRequestItemType } from 'types/procurement/enums'
import { baseUrl } from 'utils/axios'
import { SuggestedImagesModal } from './Tabs/Components/SuggestedImagesModal'
import { uploadProcurementPurchaseRequestImages } from 'api'
import { AxiosError } from 'axios'

export interface ILineItem {
  id: number
  type: PurchaseRequestItemType
  inventoryItemId: number | null
  name: string | null
  quantity: number
  unit: Option | null
  urgency: number
  images: File[]
  sku: string | null
  imageUrls?: {
    id: number
    imageUrl: string
  }[]
}
export const CreateProcurement = ({
  defaultValues,
}: {
  defaultValues?: ProcurementPurchaseRequestDetailsResponse
}) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [lineItems, setLineItems] = useState<ILineItem[]>(
    defaultValues?.items.map((item) =>
      item.type === PurchaseRequestItemType.Item
        ? {
            title: defaultValues?.title || '',
            id: item.id,
            type: item.type,
            inventoryItemId: item.inventoryItem?.id || null,
            name: item.inventoryItem?.name || null,
            quantity: item.quantity || 0,
            unit: item.unit ? { label: item.unit.name, value: item.unit.id } : null,
            urgency: item.urgency || null,
            images: [],
            sku: item.inventoryItem?.sku || null,
            imageUrls: item.images || [],
          }
        : {
            title: defaultValues?.title || '',
            id: item.id,
            type: item.type,
            name: item.name || null,
            quantity: item.quantity || 0,
            unit: item.unit ? { label: item.unit.name, value: item.unit.id } : null,
            urgency: item.urgency || null,
            images: [],
            sku: null,
            inventoryItemId: null,
            imageUrls: item.images || [],
          },
    ) || [],
  )
  const [errors, setErrors] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [values, setValues] = useState<{
    title: string
    description: string
    department: Option | null
    costCenter: Option | null
    priority: Option | null
    supplier: Option | null
    reason: string
  }>({
    title: defaultValues?.title || '',
    description: defaultValues?.notes || '',
    department: defaultValues?.department
      ? { label: defaultValues.department.name, value: defaultValues.department.id }
      : null,
    costCenter: defaultValues?.costCenter
      ? { label: defaultValues.costCenter.name, value: defaultValues.costCenter.id }
      : null,
    priority: defaultValues?.priority
      ? {
          label: PROCUREMENT_PRIORITY_META[defaultValues.priority].label,
          value: defaultValues.priority,
        }
      : null,
    supplier: defaultValues?.preferredSupplier
      ? {
          label: defaultValues.preferredSupplier.name,
          value: defaultValues.preferredSupplier.id,
        }
      : null,
    reason: defaultValues?.reason || '',
  })
  const handleAddLineItem = () => {
    setLineItems([
      ...lineItems,
      {
        id: new Date().getMilliseconds(),
        type: PurchaseRequestItemType.Text,
        inventoryItemId: null,
        name: null,
        quantity: 0,
        unit: null,
        urgency: null,
        images: [],
        imageUrls: [],
        sku: null,
      },
    ])
  }

  const handleDeleteLineItem = (index: number) => {
    setLineItems(lineItems.filter((item) => item.id !== lineItems[index].id))
  }

  const validateForm = () => {
    setErrors(false)
    if (!values.title || !values.department || !values.priority) {
      setErrors(true)
      return 'Please fill all required fields'
    }
    if (!lineItems.length) {
      setErrors(true)
      return 'Please add at least one item'
    }
    if (lineItems.some((item) => !item.name || !item.quantity || !item.unit)) {
      setErrors(true)
      return 'Please fill all item fields'
    }
    if (values.priority?.value === ProcurementPriority.Critical && !values.reason) {
      setErrors(true)
      return 'Reason is required'
    }
    return null
  }
  const constructData = () => {
    const data: CreateProcurementPurchaseRequestDto = {
      title: values.title,
      priority: values.priority?.value as ProcurementPriority,
      departmentId: values.department?.value as number,
      items: lineItems.map((item, index) => ({
        type: item.type,
        inventoryItemId: item.inventoryItemId,
        name: item.name,
        quantity: item.quantity,
        unitId: item.unit?.value as number,
        urgency: item.urgency,
        order: index + 1,
      })),
    }
    if (values.priority?.value === ProcurementPriority.Critical) {
      data.reason = values.reason
    }
    if (values.description) {
      data.notes = values.description
    }
    if (values.supplier?.value) {
      data.preferredSupplierId = values.supplier?.value as number
    }
    if (values.costCenter?.value) {
      data.costCenterId = values.costCenter?.value as number
    }
    return data
  }

  const uploadImages = async (itemId: number, requestId: number, images: File[]) => {
    if (images.length === 0) return
    const formData = new FormData()
    images.forEach((image) => {
      formData.append('images', image)
    })
    const resp = await uploadProcurementPurchaseRequestImages(requestId, itemId, formData)
    return resp
  }

  const saveDraft = async () => {
    const error = validateForm()
    if (error) throw new Error(error)
    const data = constructData()
    if (defaultValues?.id) {
      const resp = await baseUrl.patch<ProcurementPurchaseRequestListResponse>(
        `/procurement/management/purchase/request/${defaultValues.id}`,
        data,
      )
      await Promise.resolve(
        resp.data.items.forEach(async (item, index) => {
          const images = lineItems[index].images
          await uploadImages(item.id, resp.data.id, images)
        }),
      )
      return resp
    } else {
      const resp = await baseUrl.post<ProcurementPurchaseRequestListResponse>(
        '/procurement/management/purchase/request',
        data,
      )
      await Promise.all(
        resp.data.items.map(async (item, index) => {
          const images = lineItems[index].images
          await uploadImages(item.id, resp.data.id, images)
        }),
      )
      return resp
    }
  }

  const handleSaveDraft = async () => {
    const error = validateForm()
    if (error) {
      toast.error(error)
      return
    }
    try {
      setIsLoading(true)
      const resp = await saveDraft()

      toast.success('Purchase request created successfully')
      queryClient.invalidateQueries({ queryKey: ['procurement-purchase-requests', resp.data.id] })
      navigate(-1)
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.message)
      } else {
        toast.error(error.message)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleImageUpload = (index: number, files: File[]) => {
    setLineItems((prev) => {
      const newLineItems = [...prev]
      newLineItems[index] = {
        ...newLineItems[index],
        images: [...newLineItems[index]?.images, ...files],
      }
      return newLineItems
    })
  }

  const handleImageDelete = (itemIndex: number, index: number) => {
    setLineItems((prev) => {
      const newLineItems = [...prev]
      newLineItems[itemIndex] = {
        ...newLineItems[itemIndex],
        images: newLineItems[itemIndex]?.images?.filter((_, i) => i !== index),
      }
      return newLineItems
    })
  }

  const handleSubmit = async () => {
    try {
      setIsLoading(true)
      const resp = await saveDraft()
      await baseUrl.post(`/procurement/management/purchase/request/${resp.data.id}/submit`)
      toast.success('Purchase request submitted successfully')
      queryClient.invalidateQueries({ queryKey: ['procurement-purchase-requests', resp.data.id] })
      navigate(-1)
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.message)
      } else {
        toast.error(error.message)
      }
    } finally {
      setIsLoading(false)
    }
  }
  const handleItemSelect = ({
    index,
    item,
  }: {
    index: number
    item: IInventoryItemSingleResponse
  }) => {
    const newLineItems = structuredClone(lineItems)
    newLineItems[index] = {
      ...newLineItems[index],
      inventoryItemId: item.id,
      name: item.name,
      unit: item.unit ? { label: item.unit.name, value: item.unit.id } : null,
      type: PurchaseRequestItemType.Item,
      sku: item.sku,
    }
    setLineItems(newLineItems)
  }

  const handleItemNameChange = ({ index, name }: { index: number; name: string }) => {
    const newLineItems = structuredClone(lineItems)
    newLineItems[index] = {
      ...newLineItems[index],
      name,
      type: PurchaseRequestItemType.Text,
    }
    setLineItems(newLineItems)
  }

  const handleFieldChange = ({
    index,
    field,
    value,
  }: {
    index: number
    field: keyof ILineItem
    value: any
  }) => {
    const newLineItems = structuredClone(lineItems)
    newLineItems[index] = {
      ...newLineItems[index],
      [field]: value as ILineItem[keyof ILineItem],
    }
    setLineItems(newLineItems)
  }

  return (
    <>
      <NavBar />
      <Spinner loading={isLoading} />
      <PageWrapper noFooter>
        <div
          style={{
            maxWidth: '1000px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
          }}
        >
          <Flex between>
            <H1WithBack title='Create Purchase requests' arrow />
            <Flex gap='0.5rem'>
              <OutlineButton onClick={handleSaveDraft} disabled={isLoading}>
                Save Draft
              </OutlineButton>
              <Button orange disabled={isLoading} onClick={handleSubmit}>
                Submit
              </Button>
            </Flex>
          </Flex>
          <Card padding='1.25rem'>
            <CardBody>
              <Heading2 mb='0.25rem'>Purchase Details</Heading2>
              <MediumText colorVariant={ColorVariant.SECONDARY} mb='1.5rem'>
                Descriptive text goes here
              </MediumText>
              <Flex column gap='1.25rem'>
                <Input
                  label='Title'
                  name='title'
                  type='text'
                  placeholder='Enter value...'
                  onChange={(e) => setValues({ ...values, title: e.target.value })}
                  error={errors && !values.title ? 'Title is required' : undefined}
                  value={values.title}
                />
                <Flex gap='1.25rem'>
                  <DepartmentSelect
                    value={values.department}
                    onChange={(value) => {
                      setValues({ ...values, department: value })
                    }}
                    label='Department'
                    placeholder='Select department'
                    error={errors && !values.department ? 'Department is required' : undefined}
                  />
                  <CostCenterSelect
                    value={values.costCenter}
                    onChange={(value) => {
                      setValues({ ...values, costCenter: value })
                    }}
                    label='Cost Center'
                    placeholder='Select cost center'
                  />
                </Flex>
                <Flex gap='1.25rem'>
                  <Select
                    value={values.priority}
                    label='Priority'
                    placeholder='Select priority'
                    options={Object.values(ProcurementPriority)
                      .filter((priority) => !isNaN(Number(priority)))
                      .map((priority) => ({
                        label: PROCUREMENT_PRIORITY_META[priority]?.label,
                        value: priority,
                      }))}
                    onChange={(value) => {
                      setValues({ ...values, priority: value })
                    }}
                    error={errors && !values.priority ? 'Priority is required' : undefined}
                  />
                  <InventorySupplierSelect
                    value={values.supplier}
                    onChange={(value) => {
                      setValues({ ...values, supplier: value })
                    }}
                    label='Preferred Supplier'
                    placeholder='Select supplier'
                  />
                </Flex>
                <Textarea
                  label='Description'
                  name='description'
                  placeholder='Enter value...'
                  onChange={(e) => setValues({ ...values, description: e.target.value })}
                />
              </Flex>
            </CardBody>
          </Card>
          {values.priority?.value === ProcurementPriority.Critical && (
            <Card padding='1.25rem' borderLeft={colors.red}>
              <CardBody>
                <Flex between>
                  <MediumText bold>Critycal Priority - Reason required</MediumText>
                  <Badge text="BLOCKED" red />
                </Flex>
                <Alert
                  title='Critycal'
                  description='When priority is set to CRITICAL, you must explain why. This will be visible to the approvers.'
                  mt='0.75rem'
                  mb='0.75rem'
                  dismissKey='critical-priority-alert'
                />
                <Input
                  label='Why this request is critical?'
                  name='reason'
                  type='text'
                  placeholder='Enter here...'
                  onChange={(e) => setValues({ ...values, reason: e.target.value })}
                  error={
                    errors &&
                    values.priority?.value === ProcurementPriority.Critical &&
                    !values.reason
                      ? 'Reason is required'
                      : undefined
                  }
                />
              </CardBody>
            </Card>
          )}
          <Card padding='1.25rem'>
            <CardBody>
              <Flex between>
                <Flex column>
                  <Heading2 mb='0.25rem'>Items</Heading2>
                  <MediumText colorVariant={ColorVariant.SECONDARY}>
                    {lineItems.length > 0 ? ` ${lineItems.length} Items` : 'No items added'}
                  </MediumText>
                </Flex>
                <Flex gap='0.5rem'>
                  <Button orange onClick={handleAddLineItem}>
                    Add item
                  </Button>
                </Flex>
              </Flex>
              {lineItems.length > 0 && (
                <Flex column gap='1.25rem' mt='20'>
                  {lineItems.map((item, index) => (
                    <RequestLineItem
                      key={item.id}
                      item={item}
                      index={index}
                      onDelete={handleDeleteLineItem}
                      onItemSelect={handleItemSelect}
                      onItemNameChange={handleItemNameChange}
                      onFieldChange={handleFieldChange}
                      onImageUpload={handleImageUpload}
                      onImageDelete={handleImageDelete}
                    />
                  ))}
                </Flex>
              )}
            </CardBody>
          </Card>
          <Flex between>
            <OutlineButton colorVariant={ColorVariant.SECONDARY}>Cancel</OutlineButton>
            <Flex gap='0.5rem'>
              <OutlineButton disabled={isLoading} onClick={handleSaveDraft}>
                Save Draft
              </OutlineButton>
              <Button orange disabled={isLoading} onClick={handleSubmit}>
                Submit
              </Button>
            </Flex>
          </Flex>
        </div>
      </PageWrapper>
    </>
  )
}

const RequestLineItem = ({
  item,
  index,
  onDelete,
  onItemSelect,
  onItemNameChange,
  onFieldChange,
  onImageUpload,
  onImageDelete,
}: {
  item: ILineItem
  index: number
  onDelete: (index: number) => void
  onItemSelect: ({ index, item }: { index: number; item: IInventoryItemSingleResponse }) => void
  onItemNameChange: ({ index, name }: { index: number; name: string }) => void
  onFieldChange: ({
    index,
    field,
    value,
  }: {
    index: number
    field: keyof ILineItem
    value: any
  }) => void
  onImageUpload: (index: number, files: File[]) => void
  onImageDelete: (itemIndex: number, index: number) => void
}) => {
  const [suggestedImagesModalOpen, setSuggestedImagesModalOpen] = useState(false)
  console.log(item.imageUrls, 'IMAGE URLS')
  return (
    <>
      <Card borderLeft={item.type === PurchaseRequestItemType.Item ? colors.green : colors.yellow}>
        <CardBody>
          <Flex gap='1.25rem' between mb='28'>
            <Flex gap='0.5rem' center>
              <MediumText colorVariant={ColorVariant.SECONDARY}>#{index + 1}</MediumText>
              {item.type === PurchaseRequestItemType.Item ? (
                <Badge text={`SKU-${item.sku}`} green />
              ) : (
                <Flex gap='0.5rem' center>
                  <Icon size='1.25rem'>{FreeTextItemIcon}</Icon>
                  <Badge text="Free-line text" orange />
                </Flex>
              )}
            </Flex>
            <Icon fill={colors.red} size='1.25rem' onClick={() => onDelete(index)} style={{ cursor: 'pointer' }}>
              {DeleteIcon}
            </Icon>
          </Flex>
          <Flex gap='1.25rem' width='100%' mb='28'>
            <AsyncAutocomplete<IInventoryItemSingleResponse>
              inputValue={item.name}
              setInputValue={(name) => onItemNameChange({ index, name })}
              loadOptions={async (search) => {
                const data = await baseUrl.get('/inventory/item', {
                  params: {
                    q: search,
                    page: 1,
                    limit: 20,
                  },
                })
                return data.data.data
              }}
              decoder={(item) => ({
                label: item.name,
                value: item.id,
              })}
              onChange={(value) => {
                onItemSelect({ index, item: value })
              }}
              value={item.name ? { label: item.name, value: item.id } : null}
              label='Item name'
              placeholder='Enter item name'
              width='100%'
            />
          </Flex>
          <Flex gap='1.25rem' width='100%' mb='28'>
            <Select
              value={
                item.urgency
                  ? {
                      label: PROCUREMENT_PRIORITY_META[item.urgency]?.label,
                      value: item.urgency,
                    }
                  : null
              }
              label='Priority'
              placeholder='Select priority'
              options={Object.values(ProcurementPriority)
                .filter((priority) => !isNaN(Number(priority)))
                .map((priority) => ({
                  label: PROCUREMENT_PRIORITY_META[priority]?.label,
                  value: priority,
                }))}
              onChange={(value) => {
                onFieldChange({ index, field: 'urgency', value: value.value })
              }}
            />
            <Flex gap='1.25rem' width='100%'>
              <Input
                label='Quantity'
                name='quantity'
                type='number'
                placeholder='Enter value...'
                value={item.quantity}
                onChange={(e) => onFieldChange({ index, field: 'quantity', value: e.target.value })}
              />
              <UnitSelect
                value={item.unit}
                onChange={(value) => {
                  onFieldChange({ index, field: 'unit', value })
                }}
                label='Unit'
                placeholder='Select unit'
              />
            </Flex>
          </Flex>
          <Flex gap='1.25rem' width='100%' column>
            {item.imageUrls?.length > 0 &&
              item.imageUrls.map((image, index) => (
                <UploadedFile
                  file={image.imageUrl}
                  key={image + '-' + index}
                  // onDelete={() => onImageDelete(index, image)}
                />
              ))}
            {item.images?.length > 0 &&
              item.images.map((image, i) => (
                <UploadedFile
                  file={image}
                  key={image.name + '-' + i}
                  onDelete={() => onImageDelete(index, i)}
                />
              ))}
            <FileInput
              label='Images'
              name='images'
              type='file'
              leading={
                <OutlineButton size='small' onClick={() => setSuggestedImagesModalOpen(true)}>
                  <Flex gap='0.25rem' center>
                    <AiIcon />
                    Add AI image
                  </Flex>
                </OutlineButton>
              }
              multiple
              accept='image/*'
              uploadFile={(files) => {
                onImageUpload(index, files)
              }}
            />
          </Flex>
        </CardBody>
      </Card>
      {suggestedImagesModalOpen && (
        <SuggestedImagesModal
          item={item}
          onSave={(images) => {
            onImageUpload(index, images)
          }}
          onClose={() => {
            setSuggestedImagesModalOpen(false)
          }}
        />
      )}
    </>
  )
}
